import { useState } from "react";
import type { SiteOfCareInfo } from "../../types/caseView";
import { FACILITY_TYPES } from "../../utils/caseViewHelpers";

interface SiteOfCareDisplayProps {
  siteOfCareInfo: SiteOfCareInfo;
  callMode?: boolean;
  compact?: boolean;
}

export function SiteOfCareDisplay({ siteOfCareInfo, callMode = false, compact = false }: SiteOfCareDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(!compact);
  
  if (!siteOfCareInfo.facilityLabel && siteOfCareInfo.hospitalJustifications.length === 0) {
    return null;
  }

  const facility = FACILITY_TYPES.find(f => f.id === siteOfCareInfo.facilityType);

  if (compact) {
    return (
      <div className={`rounded-lg overflow-hidden ${
        callMode 
          ? "bg-amber-900/20 border border-amber-700/30" 
          : "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"
      }`}>
        {/* Compact Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
            callMode ? "hover:bg-amber-900/30" : "hover:bg-amber-100/50"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">{facility?.icon || "🏥"}</span>
            <span className={`font-medium ${callMode ? "text-amber-200" : "text-amber-800"}`}>
              Site of Care Appeal
            </span>
            <span className={`text-sm ${callMode ? "text-amber-400" : "text-amber-600"}`}>
              — {siteOfCareInfo.facilityLabel}
            </span>
          </div>
          <svg
            className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-180" : ""} ${
              callMode ? "text-amber-400" : "text-amber-500"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expanded Content */}
        {isExpanded && siteOfCareInfo.justificationLabels.length > 0 && (
          <div className={`px-4 pb-4 border-t ${callMode ? "border-amber-700/30" : "border-amber-200"}`}>
            <p className={`text-xs font-medium uppercase tracking-wide mt-3 mb-2 ${
              callMode ? "text-amber-400" : "text-amber-700"
            }`}>
              Clinical Justifications for Hospital Setting
            </p>
            <ul className="space-y-1.5">
              {siteOfCareInfo.justificationLabels.map((label, i) => (
                <li 
                  key={i}
                  className={`flex items-start gap-2 text-sm ${
                    callMode ? "text-amber-100" : "text-amber-900"
                  }`}
                >
                  <span className={callMode ? "text-amber-400" : "text-amber-500"}>•</span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Full display (non-compact)
  return (
    <div className={`rounded-xl overflow-hidden ${
      callMode 
        ? "bg-amber-900/20 border border-amber-700/30" 
        : "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"
    }`}>
      <div className="px-5 py-4">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
            callMode ? "bg-amber-800/50" : "bg-amber-100"
          }`}>
            {facility?.icon || "🏥"}
          </div>
          <div>
            <h3 className={`font-semibold ${callMode ? "text-amber-100" : "text-amber-900"}`}>
              Site of Care Appeal
            </h3>
            <p className={`text-sm ${callMode ? "text-amber-300" : "text-amber-700"}`}>
              Requesting: {siteOfCareInfo.facilityLabel}
            </p>
          </div>
        </div>

        {siteOfCareInfo.justificationLabels.length > 0 && (
          <div className={`rounded-lg p-3 ${
            callMode ? "bg-slate-800/50" : "bg-white/60"
          }`}>
            <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${
              callMode ? "text-amber-400" : "text-amber-700"
            }`}>
              Clinical Justifications
            </p>
            <ul className="space-y-2">
              {siteOfCareInfo.justificationLabels.map((label, i) => (
                <li 
                  key={i}
                  className={`flex items-start gap-2 text-sm ${
                    callMode ? "text-slate-200" : "text-slate-700"
                  }`}
                >
                  <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    callMode ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-600"
                  }`}>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}